import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  Search,
  Eye,
  Pencil,
  RotateCcw,
  Send,
  Trash2,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../Input/Input";
import APITable from "../Tables/APITable";
import Pagination from "../../components/Pagination/Pagination";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import Button from "../../components/Button/Button";
import ViewUserModal from "./ViewUserModal.jsx";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import Notification from "../../components/Notification/Notification";
import { TableSkeleton } from "../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiDelete, apiGet, apiPost, apiPut } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";
import { can } from "../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./TeamMembersSection.module.scss";

export default function TeamMembersSection({
  refreshTrigger,
  userType,
  onSummaryRefresh,
}) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusModal, setStatusModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [restoreModal, setRestoreModal] = useState(null);
  const [resendModal, setResendModal] = useState(null);

  // Toast
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const notify = useCallback((message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3500);
  }, []);

  // Search & Filters
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [roleId, setRoleId] = useState("");

  // "Deleted" is a virtual status: it maps to the deleted=true query flag.
  const showingDeleted = status === "Deleted";

  // Granted actions on the "users" resource (from /modules/sidebar)
  const canDo = (action) => can("users", action);

  // Modals
  const [viewUser, setViewUser] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  /* -------------------- AUTH USER -------------------- */
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const currentUserId = currentUser?.id;
  const orgId = currentUser?.orgId;

  /* -------------------- Fetch Roles -------------------- */
  const fetchRoles = useCallback(async () => {
    if (!userType) return;
    try {
      const query = new URLSearchParams({ type: userType }).toString();
      const res = await apiGet("user", `/role?${query}`);
      setRoles(res?.data || []);
    } catch (error) {
      console.error("Failed to fetch roles", error);
    }
  }, [userType]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  /* -------------------- Fetch Users -------------------- */
  const fetchUsers = useCallback(async () => {
    if (!userType) return;

    setLoading(true);
    try {
      const query = new URLSearchParams({
        type: userType,
        orgId,
        page,
        limit: rowsPerPage,
        ...(appliedSearch && { search: appliedSearch }),
        ...(status && status !== "Deleted" && { status }),
        ...(status === "Deleted" && { deleted: "true" }),
        ...(roleId && { roleId }),
      }).toString();

      const res = await apiGet("user", `/customer/users?${query}`);
      const apiData = res?.data;

      setUsers(apiData?.users || []);
      setTotalUsers(apiData?.pagination?.total || 0);
      setTotalPages(apiData?.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Failed to fetch users", error);
      notify(getErrorMessage(error, "Failed to fetch users"), "error");
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, status, roleId, rowsPerPage, userType, orgId, notify]);

  useEffect(() => {
    if (!userType) return;
    fetchUsers();
  }, [refreshTrigger, fetchUsers, userType]);

  /* -------------------- Table Data -------------------- */
  const tableData = users.map((user) => ({
    id: user.id,
    name: user.name,
    email: user.email,
    status: user.status,
    role: user.role?.name || "-",
    joined: user.createdAt,
    avatarText: user.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase(),
    industry: user.industry || "-",
    designation: user.designation,
    mobile: user.mobile,
    organization: user.organization,
    active: user.status === "Active",
    onView: async () => {
      setLoading(true);
      const fullProfile = await fetchUserProfile(user.id);
      setViewUser({
        ...user,
        profile_name: fullProfile,
      });
      setLoading(false);
    },
    onToggle: user.status === "Pending" ? () => {} : () => setStatusModal(user),
    onEdit: () => navigate(`/users/edit/${user.id}`),
    onDelete: () => setDeleteModal(user),
    onRestore: () => setRestoreModal(user),
    onResendInvite: () => setResendModal(user),
  }));

  const confirmToggle = async () => {
    if (!statusModal) return;

    const user = statusModal;
    const newStatus = user.status === "Active" ? "Inactive" : "Active";

    try {
      setLoading(true);
      await apiPut("user", "/users/status", {
        user_id: user.id,
        status: newStatus,
      });

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, status: newStatus } : u)),
      );

      if (user.id === currentUserId && newStatus === "Inactive") {
        localStorage.clear();
        navigate("/login", { replace: true });
        return;
      }

      fetchUsers();
      onSummaryRefresh?.();
    } catch (error) {
      console.error("Failed to update user status", error);
      notify(getErrorMessage(error, "Failed to update user status"), "error");
    } finally {
      setLoading(false);
      setStatusModal(null);
    }
  };

  const fetchUserProfile = async (userId) => {
    try {
      const res = await apiGet("user", `/profile?userId=${userId}`);
      return res?.data || null;
    } catch (err) {
      console.error("Failed to fetch user profile", err);
      return null;
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;

    try {
      setLoading(true);
      const res = await apiDelete("user", `/users/${deleteModal.id}`);
      if (res?.status === "success") {
        if (deleteModal.id === currentUserId) {
          localStorage.clear();
          navigate("/login", { replace: true });
          return;
        }

        fetchUsers();
        onSummaryRefresh?.();
      } else {
        notify(res?.message || "Failed to delete user", "error");
      }
    } catch (error) {
      console.error("Failed to delete user", error);
      notify(getErrorMessage(error, "Failed to delete user"), "error");
    } finally {
      setLoading(false);
      setDeleteModal(null);
    }
  };

  const confirmRestore = async () => {
    if (!restoreModal) return;

    try {
      setLoading(true);
      const res = await apiPost("user", `/users/${restoreModal.id}/restore`);
      if (res?.status === "success") {
        notify(res?.message || "User restored successfully", "success");
        fetchUsers();
        onSummaryRefresh?.();
      } else {
        notify(res?.message || "Failed to restore user", "error");
      }
    } catch (error) {
      console.error("Failed to restore user", error);
      notify(getErrorMessage(error, "Failed to restore user"), "error");
    } finally {
      setLoading(false);
      setRestoreModal(null);
    }
  };

  const confirmResendInvite = async () => {
    if (!resendModal) return;

    try {
      setLoading(true);
      const res = await apiPost(
        "user",
        `/users/${resendModal.id}/resend-invite`,
      );
      if (res?.status === "success") {
        notify(res?.message || "Invitation resent", "success");
      } else {
        notify(res?.message || "Failed to resend invitation", "error");
      }
    } catch (error) {
      console.error("Failed to resend invitation", error);
      notify(getErrorMessage(error, "Failed to resend invitation"), "error");
    } finally {
      setLoading(false);
      setResendModal(null);
    }
  };

  const columns = [
    { label: "User", key: "name" },
    { label: "Email", key: "email" },
    { label: "Status", key: "status" },
    { label: "Role", key: "role" },
    { label: "Joined", key: "joined" },
    { label: "Actions", key: "action" },
  ];

  /* -------------------- Filters -------------------- */
  const handleStatusChange = (e) => {
    setPage(1);
    setStatus(e.target.value);
  };

  const handleRoleChange = (e) => {
    setPage(1);
    setRoleId(e.target.value);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleSearch = () => {
    setAppliedSearch(searchInput);
    setPage(1);
  };

  /* -------------------- CSV Download -------------------- */
  const handleDownloadCSV = () => {
    const headers =
      columns
        .filter((col) => col.key !== "action")
        .map((col) => col.label)
        .join(",") + "\n";

    const csvRows = tableData
      .map((u) => [u.name, u.email, u.status, u.role, u.joined].join(","))
      .join("\n");

    const blob = new Blob([headers + csvRows], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "team_members.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* -------------------- JSX -------------------- */
  return (
    <div className={styles["admin-team-mem-sec__card"]}>
      {notification && (
        <div className={styles["admin-team-mem-sec__toast"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER & FILTERS */}
      <div className={styles["admin-team-mem-sec__header-row"]}>
        <div className={styles["admin-team-mem-sec__title-group"]}>
          <h3 className={styles["admin-team-mem-sec__title"]}>Team Members</h3>
          <p className={styles["admin-team-mem-sec__subtitle"]}>
            Manage team members and their access levels
          </p>
        </div>

        {canDo("export") && (
          <button
            type="button"
            className={styles["admin-team-mem-sec__download-btn"]}
            onClick={handleDownloadCSV}
          >
            <Download size={16} strokeWidth={2.5} />
            Download
          </button>
        )}
      </div>

      {/* TABLE CONTROLS */}
      <div className={styles["admin-team-mem-sec__controls"]}>
        <div className={styles["admin-team-mem-sec__showing-text"]}>
          Showing {(page - 1) * rowsPerPage + 1} to{" "}
          {Math.min(page * rowsPerPage, totalUsers)} of {totalUsers} users
        </div>

        <div className={styles["admin-team-mem-sec__filters"]}>
          <div className={styles["admin-team-mem-sec__search-box"]}>
            <Input
              type="search"
              placeholder="Search user, email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" icon={Search} onClick={handleSearch} />
          </div>

          <div className={styles["admin-team-mem-sec__select-group"]}>
            <Input
              type="select"
              placeholder="All Status"
              value={status}
              onChange={handleStatusChange}
              options={[
                { label: "Active", value: "Active" },
                { label: "Inactive", value: "Inactive" },
                { label: "Pending", value: "Pending" },
                { label: "Deleted", value: "Deleted" },
              ]}
            />
            <Input
              type="select"
              placeholder="All Roles"
              value={roleId}
              onChange={handleRoleChange}
              options={roles.map((role) => ({
                label: role.name,
                value: role.id,
              }))}
            />
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className={styles["admin-team-mem-sec__table-wrapper"]}>
        {loading ? (
          <TableSkeleton columns={6} rows={5} />
        ) : (
          <APITable
            columns={columns}
            data={tableData}
            renderCell={(row, key) => {
              /* AVATAR + NAME */
              if (key === "name") {
                return (
                  <div className={styles["admin-team-mem-sec__user-cell"]}>
                    <div className={styles["admin-team-mem-sec__avatar"]}>
                      {row.avatarText}
                    </div>
                    <span className={styles["admin-team-mem-sec__name"]}>
                      {row.name}
                    </span>
                  </div>
                );
              }

              /* STATUS PILL */
              if (key === "status") {
                let badgeClass = styles["admin-team-mem-sec__badge--pending"];
                if (row.status === "Active")
                  badgeClass = styles["admin-team-mem-sec__badge--active"];
                if (row.status === "Inactive" || row.status === "Deleted")
                  badgeClass = styles["admin-team-mem-sec__badge--inactive"];

                return (
                  <span
                    className={`${styles["admin-team-mem-sec__badge"]} ${badgeClass}`}
                  >
                    {row.status}
                  </span>
                );
              }

              /* ACTIONS */
              if (key === "action") {
                if (showingDeleted) {
                  return (
                    <div className={styles["admin-team-mem-sec__action-cell"]}>
                      {canDo("restore-user") ? (
                        <div
                          className={`${styles["admin-team-mem-sec__icon-btn"]} ${styles["action-view"]}`}
                          onClick={row.onRestore}
                          data-tooltip="Restore User"
                        >
                          <RotateCcw size={18} strokeWidth={2.5} />
                        </div>
                      ) : (
                        "—"
                      )}
                    </div>
                  );
                }

                return (
                  <div className={styles["admin-team-mem-sec__action-cell"]}>
                    {canDo("active-inactive-user") && (
                      <div className={styles["admin-team-mem-sec__action-cell"]}>
                        <div
                          data-tooltip={
                            row.active ? "Deactivate User" : "Activate User"
                          }
                        >
                          <ToggleSwitch
                            isOn={row.active}
                            onToggle={row.onToggle}
                          />
                        </div>
                      </div>
                    )}

                    <div
                      className={`${styles["admin-team-mem-sec__icon-btn"]} ${styles["action-view"]}`}
                      onClick={row.onView}
                      data-tooltip="View User"
                    >
                      <Eye size={18} strokeWidth={2.5} />
                    </div>

                    {canDo("update") && (
                      <div
                        className={`${styles["admin-team-mem-sec__icon-btn"]} ${styles["action-view"]}`}
                        onClick={row.onEdit}
                        data-tooltip="Edit User"
                      >
                        <Pencil size={18} strokeWidth={2.5} />
                      </div>
                    )}

                    {canDo("delete") && (
                      <div
                        className={`${styles["admin-team-mem-sec__icon-btn"]} ${styles["action-view"]}`}
                        onClick={row.onDelete}
                        data-tooltip="Delete User"
                      >
                        <Trash2 size={18} strokeWidth={2.5} />
                      </div>
                    )}

                    {row.status === "Pending" && canDo("resend-invite") && (
                      <div
                        className={`${styles["admin-team-mem-sec__icon-btn"]} ${styles["action-view"]}`}
                        onClick={row.onResendInvite}
                        data-tooltip="Resend Invite"
                      >
                        <Send size={18} strokeWidth={2.5} />
                      </div>
                    )}
                  </div>
                );
              }

              return row[key];
            }}
          />
        )}
      </div>

      {/* PAGINATION */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* MODALS */}
      <ConfirmModal
        open={!!statusModal}
        title="Are you sure you want to change the user status?"
        onClose={() => setStatusModal(null)}
        onConfirm={confirmToggle}
      />

      <ConfirmModal
        open={!!deleteModal}
        title="Are you sure you want to delete this user?"
        onClose={() => setDeleteModal(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmModal
        open={!!restoreModal}
        title="Are you sure you want to restore this user?"
        onClose={() => setRestoreModal(null)}
        onConfirm={confirmRestore}
      />

      <ConfirmModal
        open={!!resendModal}
        title="Resend the invitation email to this user?"
        onClose={() => setResendModal(null)}
        onConfirm={confirmResendInvite}
      />

      <ViewUserModal
        open={!!viewUser}
        user={viewUser}
        onClose={() => setViewUser(null)}
        fields={[
          { label: "Name", key: "name", value: (user) => user.name },
          { label: "Role", value: (user) => user.role?.name },
          { label: "Email", key: "email", value: (user) => user.email },
          { label: "Mobile", key: "mobile", value: (user) => user.mobile },
          {
            label: "Industry",
            key: "industry",
            value: (user) => user.industry,
          },
          {
            label: "Designation",
            key: "designation",
            value: (user) => user.designation,
          },
          { label: "Status", key: "status", value: (user) => user.status },
          {
            label: "Joined Date",
            key: "createdAt",
            value: (user) => user.createdAt,
          },
          {
            label: "Organization",
            key: "companyName",
            value: (user) => user.organization?.companyName,
          },
          {
            label: "State",
            key: "State",
            value: (user) => user.organization?.state,
          },
          {
            label: "Gst",
            key: "Gst",
            value: (user) => user.organization?.gstNo,
          },
          {
            label: "Created At",
            key: "createdAt",
            value: (user) => user.organization?.createdAt,
          },
          {
            label: "Address",
            key: "Address",
            value: (user) => user.organization?.address,
          },
        ]}
      />
    </div>
  );
}
